import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { RiEyeFill, RiEyeOffFill } from 'react-icons/ri';

import { Box, IconButton, Stack, Typography } from '@mui/material';

import { useTrackPortfolioAnalytics } from '@arrived/hooks';
import { AccountId, AccountOfferingBalance } from '@arrived/models';
import { useGetAccountBalanceQuery, useGetAccountOfferingBalancesQuery } from '@arrived/queries';

import { DataStateHandler } from '../../DataStateHandler';

import { PortfolioPrivacyContext } from '../common';
import { PortfolioCards } from './cards';
import { OfferingTable } from './table';

export interface PortfolioDesktopProps {
  accountId?: AccountId;
  className?: string;
  topOffset?: number;
}

export const PortfolioDesktop = ({ accountId, ...rest }: PortfolioDesktopProps) => {
  const accountBalanceState = useGetAccountBalanceQuery(accountId);
  const accountOfferingBalancesState = useGetAccountOfferingBalancesQuery(accountId);

  useTrackPortfolioAnalytics(accountBalanceState.data, accountOfferingBalancesState.data?.offerings);

  return (
    <DataStateHandler queries={[accountOfferingBalancesState]}>
      {(accountOfferingBalancesState) => (
        <PortfolioDesktopContent
          accountId={accountId}
          accountOfferingBalances={accountOfferingBalancesState.data.offerings}
          {...rest}
        />
      )}
    </DataStateHandler>
  );
};

interface PortfolioDesktopContentProps {
  accountId?: AccountId;
  accountOfferingBalances: AccountOfferingBalance[];
  className?: string;
  topOffset?: number;
}

const PortfolioDesktopContent = ({
  accountId,
  accountOfferingBalances,
  className,
  topOffset,
}: PortfolioDesktopContentProps) => {
  const { isPrivacyModeEnabled, togglePrivacyMode } = useContext(PortfolioPrivacyContext);

  return (
    <Box className={className} width="100%">
      <Stack
        className="portfolio-top"
        minHeight={400}
        justifyContent="center"
        gap={3}
        sx={{ backgroundColor: ({ palette }) => palette.neutrals.milk }}
      >
        <Stack className="portfolio-section" width="100%" maxWidth="xl" mx="auto" padding={[8, 2]} gap={3}>
          <Box className="header" display="flex" flexDirection="row" gap={1}>
            <Typography variant="h4.semibold" data-e2e="portfolio">
              <FormattedMessage id="my-portfolio" defaultMessage="My Portfolio" />
            </Typography>
            <IconButton
              className="visibility-button"
              onClick={togglePrivacyMode}
              size="medium"
              sx={{ color: ({ palette }) => palette.darkened.midnight }}
            >
              {isPrivacyModeEnabled ? <RiEyeFill /> : <RiEyeOffFill />}
            </IconButton>
          </Box>
          <PortfolioCards accountId={accountId} className="portfolio-cards" />
        </Stack>
      </Stack>
      <Box
        className="portfolio-body"
        overflow={{ base: 'auto', lg: 'unset' }}
        width="100%"
        maxWidth="xl"
        mx="auto"
        mt={3}
        padding={[8, 2]}
      >
        <Typography variant="h5.semibold" component="h3" mb={3}>
          <FormattedMessage
            id="offering-count"
            defaultMessage={`{numOfferings} {numOfferings, plural,
                one {Property}
                other {Properties}
              }`}
            values={{ numOfferings: accountOfferingBalances.length }}
          />
        </Typography>
        <OfferingTable
          accountOfferingBalances={accountOfferingBalances}
          topOffset={topOffset}
          className="offering-table"
        />
      </Box>
    </Box>
  );
};
